import { fetchWsTicket, type ScoreData } from "./api";
import { API_BASE_URL } from "../utils/config";
import { normalizeLocale } from "../utils/locale";

import {
  COGNITIVE_SYSTEM_INSTRUCTION,
  COGNITIVE_SYSTEM_INSTRUCTION_EN,
  RESPONSE_RULES,
  RESPONSE_RULES_EN,
  TONE_AND_MANNER,
} from "../prompts/consultantPersona";

import {
  ConnectionState,
  type LiveServerMessage,
  type QuestionPhase,
} from "../types";

import type { SetupPayload } from "./wsTypes";

import {
  WebSocketClient,
  WebSocketClientError,
  type WebSocketFailureDetails,
} from "./core/WebSocketClient";
import { AudioStreamManager } from "./core/AudioStreamManager";

// Constants
export const MAX_QUESTIONS = 5;
const GEMINI_VOICE_NAME = "Enceladus";
const MIC_ENABLE_DELAY = 50;

export type LiveConnectStage =
  | "idle"
  | "fetching_ticket"
  | "initializing_audio"
  | "requesting_microphone"
  | "starting_audio_input"
  | "connecting_websocket"
  | "connected";

export interface LiveConnectionErrorDetails {
  source: "websocket" | "connect";
  connectAttempt: number;
  lang: string;
  isReconnect: boolean;
  connectStage: LiveConnectStage;
  questionCount: number;
  questionPhase: QuestionPhase;
  connectionState: ConnectionState;
  ws?: WebSocketFailureDetails;
}

export class GeminiLiveService {
  private wsClient: WebSocketClient;
  private audioManager: AudioStreamManager;

  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private connectAttempt = 0;
  private connectStage: LiveConnectStage = "idle";
  private lastConnectLang = "ko";
  private lastConnectWasReconnect = false;

  // Audio Buffering
  private pausedAudioBuffers: AudioBuffer[] = [];

  public isPaused: boolean = false;
  private pendingUserLogFlush: boolean = false;
  private pendingTurnCompleteForUi = false;
  private framesSentInThisQuestion = 0;

  // Buffers
  private userTextBuffer: string = "";
  private aiTextBuffer: string = "";
  private bargeInTextBuffer: string = "";

  public isModelSpeaking: boolean = false;
  private modelSpeaking: boolean = false; // Internal flag

  private disconnecting = false;
  private scoreData: ScoreData | null = null;

  // Question State
  public questionCount: number = 0;
  public maxQuestions: number = MAX_QUESTIONS;
  public questionPhase: QuestionPhase = "idle";

  // Callbacks
  public onStateChange: (state: ConnectionState) => void = () => {};
  public onQuestionCountChange: (count: number) => void = () => {};
  public onQuestionPhaseChange: (phase: QuestionPhase) => void = () => {};
  public onModelSpeakingChange: (speaking: boolean) => void = () => {};
  public onAudioLevel: (level: number) => void = () => {};
  public onError: (
    error: string,
    details?: LiveConnectionErrorDetails,
  ) => void = () => {};

  constructor() {
    this.wsClient = new WebSocketClient();
    this.audioManager = new AudioStreamManager();

    // Delegate WS callbacks
    this.wsClient.onStateChange = (state) => {
      this.connectionState = state;
      this.onStateChange(state);
    };

    this.wsClient.onMessage = (message) => {
      this.handleOnMessage(message);
    };

    this.wsClient.onError = (error, details) => {
      this.onError(error, this.createErrorContext("websocket", details));
    };

    this.wsClient.onSetupComplete = () => {
      this.handleOnOpen();
    };

    // Delegate Audio callbacks
    this.audioManager.onAudioData = (base64) => {
      // Only send if listening
      if (this.questionPhase === "listening") {
        this.framesSentInThisQuestion++;
        this.wsClient.send({
          type: "realtime_input",
          realtime_input: {
            media: {
              mime_type: "audio/pcm;rate=16000",
              data: base64,
            },
          },
        });
      }
    };

    this.audioManager.onAudioLevel = (level) => {
      this.onAudioLevel(level);
    };

    this.audioManager.onQueueEmpty = () => {
      setTimeout(() => this.maybeFinishAnswerUi(), 50);
    };
  }

  public setScoreData(data: ScoreData) {
    this.scoreData = data;
  }

  private setQuestionPhase(phase: QuestionPhase) {
    this.questionPhase = phase;
    this.onQuestionPhaseChange(phase);
  }

  public async connect(lang: string = "ko", isReconnect: boolean = false) {
    await this.disconnect();

    this.connectAttempt += 1;
    this.lastConnectLang = lang;
    this.lastConnectWasReconnect = isReconnect;
    this.updateState(ConnectionState.CONNECTING);
    this.resetSession(isReconnect);

    try {
      this.connectStage = "fetching_ticket";
      const ticketData = await fetchWsTicket();
      const ticket = ticketData.ticket;
      if (!isReconnect) {
        this.maxQuestions = ticketData.totalQuestionQuota ?? MAX_QUESTIONS;
        this.questionCount = ticketData.usedQuestionCount ?? 0;
        this.onQuestionCountChange(this.questionCount);
      }

      const apiBase = import.meta.env.DEV
        ? "wss://www.localtest.me"
        : API_BASE_URL.replace(/^http/, "ws");
      const wsUrl = `${apiBase}/ws/gemini-live?ticket=${encodeURIComponent(ticket)}`;

      // Initialize Audio Manager
      this.connectStage = "initializing_audio";
      await this.audioManager.initialize();
      this.connectStage = "requesting_microphone";
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.connectStage = "starting_audio_input";
      await this.audioManager.startInput(stream);

      // Setup Payload Construction
      const setupPayload = this.createSetupPayload(lang);

      this.connectStage = "connecting_websocket";
      await this.wsClient.connect(wsUrl, setupPayload);
      this.connectStage = "connected";
    } catch (e: unknown) {
      this.updateState(ConnectionState.ERROR);
      const errorMessage = e instanceof Error ? e.message : "Connection failed";

      if (e instanceof WebSocketClientError) {
        return;
      }

      this.onError(errorMessage, this.createErrorContext("connect"));
    }
  }

  private createSetupPayload(lang: string): SetupPayload {
    // i18n.language는 "en-US" 같은 지역 코드가 붙어올 수 있어 서버 전달 전에 정규화한다.
    const locale = normalizeLocale(lang);
    const isKorean = locale === "ko";
    const systemInstruction = isKorean
      ? COGNITIVE_SYSTEM_INSTRUCTION
      : COGNITIVE_SYSTEM_INSTRUCTION_EN;
    const responseRules = isKorean ? RESPONSE_RULES : RESPONSE_RULES_EN;

    let scoreInfo = "";
    if (isKorean) {
      scoreInfo = `
            사용자의 뇌인지(BPT) 검사 결과 지수는 다음과 같습니다:
            - 인지성향: ${this.scoreData?.score.lbi ?? "N/A"}
            - 인지환경: ${this.scoreData?.score.rbi ?? "N/A"}
            - 신체활동성: ${this.scoreData?.score.pei ?? "N/A"}
            - 진취성: ${this.scoreData?.score.ai ?? "N/A"}
            - 사회성: ${this.scoreData?.score.hi ?? "N/A"}
            - 실행성: ${this.scoreData?.score.wci ?? "N/A"}
            - 수학: ${this.scoreData?.score.math ?? "N/A"}
        `;
    } else {
      scoreInfo = `
            User's Cognitive Pattern (BPT) Test Results:
            - Cognitive Pattern: ${this.scoreData?.score.lbi ?? "N/A"}
            - Cognitive Environment: ${this.scoreData?.score.rbi ?? "N/A"}
            - Physical Activity: ${this.scoreData?.score.pei ?? "N/A"}
            - Progressiveness: ${this.scoreData?.score.ai ?? "N/A"}
            - Sociability: ${this.scoreData?.score.hi ?? "N/A"}
            - Execution Ability: ${this.scoreData?.score.wci ?? "N/A"}
            - Math: ${this.scoreData?.score.math ?? "N/A"}
        `;
    }

    return {
      // 멘트 서버가 응답 언어를 결정하는 값. 누락 시 서버가 "ko"로 폴백한다.
      lang: locale,
      // Gemini API Models: https://ai.google.dev/gemini-api/docs/models
      model: "models/gemini-2.5-flash-native-audio-preview-12-2025",
      generation_config: {
        response_modalities: ["AUDIO"],
        speech_config: {
          voice_config: {
            prebuilt_voice_config: { voice_name: GEMINI_VOICE_NAME },
          },
        },
      },
      system_instruction: {
        parts: [
          {
            text: `
            ${systemInstruction}
            
            ${scoreInfo}

            ${TONE_AND_MANNER}
            
            ${responseRules}

            IMPORTANT: 
            - YOU MUST RESPOND IN THE LANGUAGE SPECIFIED BELOW.
            - RESPONSE LANGUAGE: ${isKorean ? "Korean (한국어)" : "English"}
            `,
          },
        ],
      },
    };
  }

  public async disconnect() {
    if (this.disconnecting) return;
    this.disconnecting = true;

    try {
      this.wsClient.disconnect();
      this.audioManager.stopAll();
      this.pausedAudioBuffers.length = 0;
      this.connectStage = "idle";
    } finally {
      this.disconnecting = false;
    }
  }

  private updateState(state: ConnectionState) {
    this.connectionState = state;
    this.onStateChange(state);
  }

  private resetSession(isReconnect: boolean = false) {
    if (!isReconnect) {
      this.questionCount = 0;
      this.onQuestionCountChange(0);
    }
    this.connectStage = "idle";
    this.questionPhase = "idle";
    this.onQuestionPhaseChange("idle");
    this.pendingTurnCompleteForUi = false;
    this.pendingUserLogFlush = false;
    this.framesSentInThisQuestion = 0;
    this.userTextBuffer = "";
    this.aiTextBuffer = "";
    this.modelSpeaking = false;
    this.isModelSpeaking = false;
    this.onModelSpeakingChange(false);
    this.bargeInTextBuffer = "";
    this.isPaused = false;
    this.pausedAudioBuffers.length = 0;
  }

  private handleOnOpen() {
    // 오디오는 connect()에서 이미 시작되었습니다.
    this.connectStage = "connected";
  }

  private createErrorContext(
    source: LiveConnectionErrorDetails["source"],
    ws?: WebSocketFailureDetails,
  ): LiveConnectionErrorDetails {
    return {
      source,
      connectAttempt: this.connectAttempt,
      lang: this.lastConnectLang,
      isReconnect: this.lastConnectWasReconnect,
      connectStage: this.connectStage,
      questionCount: this.questionCount,
      questionPhase: this.questionPhase,
      connectionState: this.connectionState,
      ws,
    };
  }

  // --- 유틸리티 및 상태 관리 헬퍼 메서드 ---

  /**
   * 실시간 상담 일시정지 및 재개 토글
   */
  public async togglePauseResume() {
    if (this.isPaused) {
      await this.audioManager.resume();
      if (this.questionPhase === "listening") {
        await this.audioManager.resumeInput();
      }
      this.isPaused = false;
      this.flushPausedAudioBuffers();
      // 일시정지를 풀었을 때, 만약 이미 대기 중인 오디오가 없었다면 바로 상태 종료 체크
      this.maybeFinishAnswerUi();
    } else {
      await this.audioManager.suspend();
      // 참고: 녹음을 완전히 중단하려면 suspendInput을 사용합니다.
      // 보통 서스펜드 시 마이크 입력 로직도 함께 차단합니다.
      if (this.questionPhase === "listening") {
        await this.audioManager.suspendInput();
      }
      this.isPaused = true;
      this.onAudioLevel(0);
    }
  }

  public async startQuestion() {
    if (this.connectionState !== ConnectionState.CONNECTED) return;
    if (this.questionCount >= this.maxQuestions) return;
    if (this.questionPhase !== "idle") return;
    if (this.isPaused) return;

    this.pendingTurnCompleteForUi = false;
    this.pendingUserLogFlush = false;
    this.framesSentInThisQuestion = 0;

    // Resume input specifically
    await this.audioManager.resumeInput();
    this.setQuestionPhase("listening");

    this.wsClient.send({
      type: "realtime_input",
      realtime_input: { activity_start: {} },
    });
  }

  public async endQuestion() {
    if (this.connectionState !== ConnectionState.CONNECTED) return;
    if (this.questionPhase !== "listening") return;
    if (this.isPaused) return;

    if (this.framesSentInThisQuestion === 0) return;

    this.setQuestionPhase("answering");
    if (this.questionCount < this.maxQuestions) {
      this.questionCount++;
      this.onQuestionCountChange(this.questionCount);
    }

    this.pendingUserLogFlush = true;

    await this.audioManager.suspendInput();
    await new Promise((r) => setTimeout(r, MIC_ENABLE_DELAY));

    this.wsClient.send({
      type: "realtime_input",
      realtime_input: { activity_end: {} },
    });
  }

  private async handleOnMessage(message: LiveServerMessage) {
    this.handleTranscriptions(message.serverContent);
    this.handleModelTurn(message.serverContent);
    await this.handleAudioOutput(message.serverContent?.modelTurn);
  }

  private handleTranscriptions(
    serverContent: LiveServerMessage["serverContent"],
  ) {
    if (!serverContent) return;
    if (serverContent.inputTranscription) {
      const text = serverContent.inputTranscription.text || "";
      if (text.trim()) {
        if (this.modelSpeaking) {
          this.bargeInTextBuffer += text;
        } else {
          this.userTextBuffer += text;
        }
      }
    }
    if (serverContent.outputTranscription) {
      const text = serverContent.outputTranscription.text || "";
      this.aiTextBuffer += text;
    }
  }

  private handleModelTurn(serverContent: LiveServerMessage["serverContent"]) {
    if (!serverContent) return;

    if (serverContent.modelTurn && !this.modelSpeaking) {
      this.modelSpeaking = true;
      this.isModelSpeaking = true;
      this.onModelSpeakingChange(true);
      this.flushUserLogIfNeeded();
    }

    if (serverContent.turnComplete) {
      this.pendingTurnCompleteForUi = true;
      this.saveAiLog();
      this.bargeInTextBuffer = "";
      this.maybeFinishAnswerUi();
    }

    if (serverContent.interrupted) {
      this.saveAiLog();
      this.audioManager.clearAudioQueue();
      this.setQuestionPhase("idle");
      this.modelSpeaking = false;
      this.isModelSpeaking = false;
      this.onModelSpeakingChange(false);
      this.pendingTurnCompleteForUi = false;
      this.bargeInTextBuffer = "";
    }
  }

  private async handleAudioOutput(
    modelTurn: NonNullable<LiveServerMessage["serverContent"]>["modelTurn"],
  ) {
    if (!modelTurn) return;
    const parts = modelTurn.parts ?? [];
    const audioChunks = parts
      .map((p) => p.inlineData?.data)
      .filter((d): d is string => typeof d === "string" && d.length > 0);

    if (audioChunks.length === 0 || !this.audioManager.hasOutputContext) return;

    for (const base64Audio of audioChunks) {
      try {
        const audioBuffer =
          await this.audioManager.decodeAudioBuffer(base64Audio);
        if (this.isPaused) {
          this.pausedAudioBuffers.push(audioBuffer);
          continue;
        }
        this.playAudioBuffer(audioBuffer);
      } catch (e) {
        console.error("Audio decoding error", e);
      }
    }
  }

  private playAudioBuffer(audioBuffer: AudioBuffer) {
    this.audioManager.playAudioBuffer(audioBuffer);
  }

  private flushPausedAudioBuffers() {
    this.pausedAudioBuffers.forEach((buffer) => this.playAudioBuffer(buffer));
    this.pausedAudioBuffers.length = 0;
  }

  private saveAiLog() {
    if (!this.aiTextBuffer.trim()) return;
    this.aiTextBuffer = "";
  }

  private flushUserLogIfNeeded() {
    // 질문/답변 저장은 백엔드(GeminiLiveHandler)가 WebSocket 트랜스크립션으로 처리한다.
    // 여기서는 클라이언트측 버퍼/플래그만 정리한다.
    if (this.pendingUserLogFlush) {
      this.userTextBuffer = "";
      this.pendingUserLogFlush = false;
    }
  }

  private maybeFinishAnswerUi() {
    // 아직 재생할 버퍼가 남았거나, 재생 중이거나, 일시정지 상태라면 종료 처리 방지
    if (
      this.isPaused ||
      this.pausedAudioBuffers.length > 0 ||
      !this.audioManager.isQueueEmpty
    ) {
      return;
    }

    // 서버로부터 turnComplete를 받았고, 현재 재생 중인 오디오가 없는지 확인
    if (this.pendingTurnCompleteForUi) {
      this.pendingTurnCompleteForUi = false;
      this.modelSpeaking = false;
      this.isModelSpeaking = false;
      this.onModelSpeakingChange(false);
      this.setQuestionPhase("idle");
    }
  }
}

export const liveService = new GeminiLiveService();
